import { useTheme } from '@emotion/react';
import { getThemeColors, PaletteName } from './ThemeColors';
/**
 * An emotion styling function that returns a value based on the current palette name.
 * All values are optional, but you should provide a value for each citrix-ui theme in
 * `PaletteName` to avoid falling back to an unexpected color value.
 *
 * @example
 * const myBackgroundColor = fromThemePalette({ citrixbrand: '#003D48', neutral: '#0045DB' });
 * const StyledComponent = styled.div`
 *   background-color: ${myBackgroundColor};
 * `;
 */
export function fromThemePalette(values) {
  return function (props) {
    var themeWithDefaults = fromEmotionTheme(props.theme);
    return values[themeWithDefaults.paletteName] || values[Object.keys(values)[0]] || '#B1B1B1';
  };
}
/*
 * React Hook that returns a value based on the current palette name.
 * All values are optional, but you should provide a value for each citrix-ui theme in
 * `PaletteName` to avoid falling back to an unexpected color value.
 *
 * @example
 * const backgroundColor = useThemePalette({ citrixbrand: '#003D48', neutral: '#0045DB' });
 * <Text color={backgroundColor}>Hello World</Text>
 */

export function useThemePalette(values) {
  return fromThemePalette(values)({
    theme: useTheme()
  });
}

/**
 * An emotion styling function that returns the specified theme color.
 *
 * @example
 * const StyledComponent = styled.div`
 *   background-color: ${fromThemeColors('primary')};
 * `;
 */
export function fromThemeColors(color) {
  return function (props) {
    var themeWithDefaults = fromEmotionTheme(props.theme);
    return themeWithDefaults.themeColors[color];
  };
}

/**
 * Provides type-safe read access to the current citrix-ui theme as
 * provided by emotion.
 *
 * @example
 * export const MyThemedComponent = styled.div`
 * 	background-color: ${props => fromEmotionTheme(props.theme).themeColors.inputBackground};
 * `;
 */
export function fromEmotionTheme(emotionTheme) {
  return emotionTheme.citrixui || createTheme();
}

/**
 * Provides type-safe write access for the current citrix-ui theme as
 * managed by emotion. This is expected to be needed mostly in unit tests.
 *
 * @example
 * import { ThemeProvider } from '@emotion/react';
 * import { Theme, toEmotionTheme } from '@citrite/citrix-ui';
 *
 * export function wrapWithCitrixUITheme(element: React.ReactElement, citrixUiTheme: Theme) {
 * 	return <ThemeProvider theme={toEmotionTheme(citrixUiTheme)}>{element}</ThemeProvider>;
 * }
 */
export function toEmotionTheme(citrixUiTheme) {
  return {
    citrixui: citrixUiTheme
  };
}
/**
 * React hook that returns the specified theme color.
 */
export function useThemeColor(color) {
  return fromThemeColors(color)({
    theme: useTheme()
  });
}
export function createTheme(themeConfig) {
  var existingTheme = getThemeFromWindow();
  if (!themeConfig && existingTheme) {
    return existingTheme;
  }
  var paletteName = (themeConfig === null || themeConfig === void 0 ? void 0 : themeConfig.paletteName) || PaletteName.neutral;
  var themeColors = getThemeColors(paletteName, (themeConfig === null || themeConfig === void 0 ? void 0 : themeConfig.themeColorOverrides) || {
    activeTab: themeConfig === null || themeConfig === void 0 ? void 0 : themeConfig.accentColor
  });
  var themeWithDefaults = {
    paletteName: paletteName,
    accentColor: (themeConfig === null || themeConfig === void 0 ? void 0 : themeConfig.accentColor) || themeColors.primary,
    themeColors: themeColors
  };
  return themeWithDefaults;
}
function getThemeFromWindow() {
  var _citrixui_internal__, _citrixui_internal__$;
  if (typeof window === 'undefined') {
    return undefined;
  }
  return (_citrixui_internal__ = window.__citrixui_internal__) === null || _citrixui_internal__ === void 0 ? void 0 : (_citrixui_internal__$ = _citrixui_internal__.configuration) === null || _citrixui_internal__$ === void 0 ? void 0 : _citrixui_internal__$.theme;
}